import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Box,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Drawer,
} from '@mui/material';
import exportFromJSON from 'export-from-json';
import CircularProgress from '@mui/material/CircularProgress';
import { getApplicantsList } from 'api/admin';
import dayjs from 'dayjs';
import { useSnackbar } from 'components/snackbar';
import axios from 'axios';
import ApplicantProfile from './ApplicantProfile';
import { reorderWorkHistory } from 'utils/Utils';

/**
 * Component to display all applicants for a job posting.
 * 
 * @param {Object} props - Component props.
 * @param {Function} props.setSelectedApplicant - Function to set the selected applicant.
 * @param {Object} props.jobDetails - Job details object.
 * @returns {ReactElement} The AllApplicantsList component.
 */
const AllApplicantsList = ({ jobDetails }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const fullContainer = useRef(null);
  const applicantProfileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBlurLoading, setIsBlurLoading] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showApplicantProfile, setShowApplicantProfile] = useState(false);

  useEffect(() => {
    fetchApplicants();
  }, []);

  useLayoutEffect(() => {
    if (fullContainer?.current && applicantProfileRef?.current) {
      fullContainer.current.scrollTo({
        top: applicantProfileRef.current.offsetTop,
        block: 'center',
        behavior: 'smooth',
      });
    }
  }, [showApplicantProfile, selectedApplicant]);

  /**
   * Fetches the applicants list for the given job ID, and updates the local state.
   * 
   * @returns {undefined}
   */
  const fetchApplicants = async () => {
    try {
      const response = await getApplicantsList({ id });
      if (response?.success && response.data != null && response.data.applicant != null) {
        setApplications(response.data);
        console.log(response.data);
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
    setLoading(false);
  };

  const downloadSelectedApplicationsResume = async () => {
    setIsBlurLoading(true);
    setIsLoading(true);
    const application_ids = Array.from(new Set(selectedApplications));
    if (application_ids.length === 0) {
      enqueueSnackbar("No applications selected", { variant: 'error' });
      setIsLoading(false);
      return;
    }
    try {
      const response = await axios.post(`${process.env.REACT_APP_END_POINT}/api/services/download-combined-resumes`,
        { application_ids: application_ids, },
        { responseType: 'blob' }
      );
      if (response?.status === 200) {
        const blob = response.data;
        const blobUrl = URL.createObjectURL(blob);
        const formattedFileName = `combined_resumes_${new Date().toISOString().split('T')[0]}.pdf`;
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", formattedFileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } else {
        enqueueSnackbar('This uploaded file no longer exists on the server.', { variant: 'error' });
        throw new Error(`Server responded with ${response.status} `);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setIsLoading(false);
      setIsBlurLoading(false);
    }
  }

  /**
   * Downloads a CSV file with the selected applications data, including the
   * candidate's profile information, education, work experience, skills, and
   * job preferences.
   * 
   * @returns {undefined}
   */
  const downloadSelectedApplicationsData = () => {
    const selectedApplicationsData = applications.applicant.filter(application => selectedApplications.includes(application._id));
    const experienceStr = jobDetails['experience'];
    const experienceArray = experienceStr ? experienceStr.split('-').map(s => s.trim()) : [];
    const min = experienceArray[0] || '0';
    const max = experienceArray[1] || experienceArray[0] || '0';

    const applicationList = selectedApplicationsData.map(application => {
      console.log(application);
      // const work = application?.requirements_matched?.work_history?.[0];
      const score = application?.candidate_score;
      const tjScore = application?.tj_score;
      const workHistory = reorderWorkHistory(application?.requirements_matched?.work_history).filter(work => work?.organization_name !== 'Work Gap') || [];
      const work = workHistory[0];

      const scoreLabels = {
        4: 'Highly suitable',
        3: 'Highly suitable',
        2: 'Moderately suitable',
        1: 'Not suitable',
      };

      let overAllSuitability = tjScore ? scoreLabels[tjScore] : 'N/A';

      let candidateSuitability = 'N/A';
      if (score) {

        const fields = {
          qualification: 'Qualification',
          skillMatchRating: 'Skill Matching',
          salaryMatchRating: 'Salary Matching',
          experienceMatchRating: 'Experience Matching',
          industry: 'Industry',
          location: 'Location',
          format: 'Format',
          type: 'Type',
          shift: 'Shift',
          notice: 'Notice Period',
          availableHour: 'Available Hours',
        };
        candidateSuitability = Object.entries(fields).map(([key, label]) => {
          const value = score[key];
          const rating = scoreLabels[value] || 'N/A';
          return `${label}: ${rating} `;
        }).join(', ');
      }

      let experienceYears = 'N/A';
      if (work?.work_history_from) {
        const start = dayjs(work.work_history_from);
        const end = work.work_history_to ? dayjs(work.work_history_to) : (work.present ? dayjs() : null);
        if (end) {
          const months = end.diff(start, 'month');
          experienceYears = `${(months / 12).toFixed(1)} yrs`;
        }
      }

      const resumeLink = (() => {
        if (jobDetails?.external_link) return 'N/A';
        const isUploaded = application?.resume_data?.title === 'Uploaded Resume';
        const baseUrl = process.env.REACT_APP_END_POINT;
        if (isUploaded && application?.resume_data?.link) {
          const downloadLink = application.resume_data.link.replace('/fetch/', '/download/');
          return `=HYPERLINK("${baseUrl}${downloadLink}", "Download Resume")`;
        }
        if (application?._id) {
          return `=HYPERLINK("${baseUrl}/api/services/download-resume?application_id=${application._id}", "Download Resume")`;
        }
        return 'N/A';
      })();
      return {
        'Job Id': jobDetails.jobid || 'N/A',
        'Date of posting job': dayjs(jobDetails.createdAt).format('DD MMM, YYYY') || 'N/A',
        'Company Name': jobDetails.company || 'N/A',
        'Job Summary': jobDetails.job_overview || 'N/A',
        'Designation': jobDetails?.role || 'N/A',
        'Function': jobDetails?.function || 'N/A',
        'Job location': jobDetails?.location || 'N/A',
        'Job Type': jobDetails?.job_type || 'N/A',
        'Gender preference': jobDetails?.gender_preference || 'N/A',
        'disability preference': jobDetails?.disability_preference || 'N/A',
        'Education requirement': Array.isArray(jobDetails?.qualifications)
          ? jobDetails.qualifications.join(', ')
          : jobDetails?.qualifications || 'N/A',
        'Min experience': min,
        'Max experience': max,
        'Max compensation': jobDetails?.job_type === 'Full time' ? jobDetails?.salary : 'N/A',
        'Compensation Type': jobDetails?.job_type === 'Full time' ? jobDetails?.compensation || 'N/A' : jobDetails?.compensation_model || 'N/A',
        'Company Part-time Hourly rate': jobDetails?.job_type === 'Full time' ? 'N/A' : jobDetails?.salary || 'N/A',
        'Part-time - weekly commitment (hours)': jobDetails?.weekly_time_commitment || 'N/A',
        'Required skills': Array.isArray(jobDetails?.key_skills) ? jobDetails.key_skills.join(', ') : jobDetails?.key_skills || 'N/A',
        'Company Industry': jobDetails?.industry_sector || 'N/A',
        'Company Notice Period': jobDetails?.notice_period || 'N/A',
        'Job status': jobDetails?.status || 'N/A',
        'Candidate Id': application?.candidate_id?._id || 'N/A',
        'Date of job application': dayjs(application?.createdAt).format('DD MMM, YYYY') || 'N/A',
        'Candidate name': application?.candidate_profile?.full_name || 'N/A',
        'Overall suitability': overAllSuitability,
        'Parameter-wise suitability': candidateSuitability,
        'Gender': application?.candidate_profile?.sex || 'N/A',
        'Disability': application?.candidate_profile?.disability || 'N/A',
        'Date of Birth': dayjs(application?.candidate_profile?.date_of_birth).format('DD MMM, YYYY') || 'N/A',
        'Phone number': application?.candidate_profile?.phone_number || 'N/A',
        'Email id': application?.candidate_profile?.email || 'N/A',
        'LinkedIn URL': application?.candidate_profile?.linkedin_profile || 'N/A',
        'Last education(Institution Name)': application?.requirements_matched?.education?.[0]?.education_institute_name || 'N/A',
        'Last education(degree name)': (application?.requirements_matched?.qualifications || []).join(', ') || 'N/A',
        'Last education start and end dates': application?.requirements_matched?.education?.[0]?.education_from &&
          application?.requirements_matched?.education?.[0]?.education_to
          ? `${dayjs(application.requirements_matched.education[0].education_from).format('MMM YYYY')} - ${dayjs(application.requirements_matched.education[0].education_to).format('MMM YYYY')} `
          : 'N/A',
        'Work experience(Current Company Name)': work?.organization_name || 'N/A',
        'Current Designation': work?.highest_designation || 'N/A',
        'Current Job Function': work?.last_job_function || 'N/A',
        'Experience(yrs)': experienceYears,
        'Job Type Preference': (() => {
          const jobType = application?.requirements_matched?.job_type?.[0];
          if (!jobType) return 'N/A';
          const value = jobType.trim().toLowerCase();
          if (value === 'both') return 'Full Time, Part Time, Temporary, Internship';
          if (value === 'Full time') return 'Full Time';
          if (value === 'Part time') return 'Part Time, Temporary, Internship';
          return jobType;
        })(),
        'Job Location Preferences': Array.isArray(application?.requirements_matched?.location)
          ? application.requirements_matched.location.join(', ')
          : application?.requirements_matched?.location || 'N/A',
        'People managed': work?.team_size_managed || 'N/A',
        'Last work experience start and end dates': (() => {
          const start = work?.work_history_from ? dayjs(work.work_history_from).format('MMM YYYY') : 'N/A';
          const end = work?.work_history_to
            ? dayjs(work.work_history_to).format('MMM YYYY')
            : work?.present
              ? 'Present'
              : 'N/A';
          return `${start} - ${end} `;
        })(),
        'Total work experience(Years)': application?.requirements_matched?.experience || 'N/A',
        'Expertise areas': [
          application?.requirements_matched?.educations?.[0]?.all_skills,
          application?.requirements_matched?.work_history?.[0]?.all_skills,
          application?.requirements_matched?.certificates?.[0]?.all_skills
        ].filter(Boolean).join(', ') || 'N/A',
        'Professional Summary': application?.resume_data?.summary || 'N/A',
        'Resume Link': resumeLink,
        "Candidate's application status": application?.status || 'N/A',
        'Candidate feedback': application?.feedback?.map(feedback => feedback?.feedback) || 'N/A',
        'Current compensation - Fixed': application?.candidate_details?.job_preferences?.fixed_ctc || 'N/A',
        'Current compensation - variable': application?.candidate_details?.job_preferences?.variable_ctc || 'N/A',
        'Total current compensation': jobDetails?.job_type === 'Full time' ? application?.requirements_matched?.salary || application?.candidate_details?.job_preferences?.total_ctc : application?.candidate_details?.job_preferences?.total_ctc || 'N/A',
        'Expected annual compensation': application?.candidate_details?.job_preferences?.expected_ctc || 'N/A',
        'Notice period': application?.candidate_details?.job_preferences?.notice_period || 'N/A',
        'Part - time Hourly rate': jobDetails?.job_type !== 'Full time' ? application?.requirements_matched?.salary || application?.candidate_details?.job_preferences?.hourly_rate || 'N/A' : application?.candidate_details?.job_preferences?.hourly_rate || 'N/A',
        'Part - time availability(hours / week)': application?.candidate_details?.job_preferences?.hour || 'N/A',
      };
    });

    const fileName = `${jobDetails.title}_${jobDetails.jobid}_${Date.now()} _applicants`;
    const exportType = exportFromJSON.types.csv;
    exportFromJSON({ data: applicationList, fileName, exportType });
  };

  /**
   * Handles the checkbox change event for a particular application.
   * If the checkbox is checked, it adds the application id to the selectedApplications state.
   * If the checkbox is unchecked, it removes the application id from the selectedApplications state.
   * @param {object} event - The checkbox change event.
   * @param {string} applicationId - The id of the application.
   */
  const handleCheckboxChange = (event, applicationId) => {
    if (event.target.checked) {
      setSelectedApplications([...selectedApplications, applicationId]);
    } else {
      setSelectedApplications(selectedApplications.filter((_id) => _id !== applicationId));
    }
  };


  /**
   * Handles the select all checkbox change event.
   * If the checkbox is checked, it sets the selectedApplications state to include all application ids.
   * If the checkbox is unchecked, it clears the selectedApplications state.
   * @param {object} event - The select all checkbox change event.
   */
  const handleSelectAllChange = (event) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      setSelectedApplications(applications.applicant.map((application) => application._id));
    } else {
      setSelectedApplications([]);
    }
  };

  /**
   * Downloads a file from the given URL and saves it as a PDF with the given user's name.
   * If the file URL or user is missing, it logs an error message to the console.
   * If the download fails, it logs an error message to the console.
   * @param {string} fileUrl - The URL of the file to be downloaded.
   * @param {object} user - The user object containing the name to be used for the file name.
   */
  const handleDownload = async (fileUrl, user) => {
    if (!fileUrl || !user?.full_name) {
      console.error("File URL is missing");
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_END_POINT}${fileUrl} `);
      if (!response.ok) {
        enqueueSnackbar('This uploaded file no longer exists on the server.', { variant: 'error' });
        throw new Error(`Server responded with ${response.status} `);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const formattedFileName = `${user.full_name.trim()} (Uploaded Resume).pdf`;
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", formattedFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  /**
     * Handles the selection of an applicant by toggling the visibility of the
     * ApplicantProfile component and setting the selected applicant state.
     * @param {Object} applicant - The selected applicant object
     */
  const handleSelectApplicant = (applicant) => {
    setShowApplicantProfile(true);
    setSelectedApplicant(applicant);
  };
  /**
     * Handles the go back action by hiding the ApplicantProfile component.
     * @returns {undefined}
     */
  const handleGoBack = () => {
    setShowApplicantProfile(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ marginBottom: '1rem' }}>
        <Container>
          <Paper elevation={3} sx={{ padding: '1rem' }}>
            <Box >
              <Container style={{ marginTop: (jobDetails ? '20px' : '80px') }}>
                <Box sx={{ margin: '2rem 0' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h5" component="div" sx={{ marginBottom: '1rem' }}>
                      Applicants - TJ Jobs
                    </Typography>
                    <Button variant="contained" onClick={downloadSelectedApplicationsResume} disabled={selectedApplications.length === 0 || isLoading}>
                      Download Applicant Profiles
                    </Button>
                    <Button variant="contained" onClick={downloadSelectedApplicationsData} disabled={selectedApplications.length === 0 || isLoading} >
                      Download Applicant List
                    </Button>
                  </Stack>
                  <Paper elevation={3}>
                    <div style={{ height: 'auto', width: '100%' }}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Checkbox checked={selectAll} onChange={handleSelectAllChange} />
                              </TableCell>
                              <TableCell>#</TableCell>
                              <TableCell>Name</TableCell>
                              <TableCell>Match</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Education</TableCell>
                              <TableCell>Years of Experience</TableCell>
                              <TableCell>Salary</TableCell>
                              {!jobDetails?.external_link && (
                                <TableCell>Resume</TableCell>
                              )}
                              <TableCell>Candidate Profile</TableCell>
                            </TableRow>
                          </TableHead>
                          {/* applications.applicant listing */}
                          <TableBody>
                            {applications.applicant && applications.applicant.length > 0 ? (
                              applications.applicant.map((application, index) => (
                                <TableRow key={application?._id || index} onClick={() => handleSelectApplicant(application)} style={{ cursor: 'pointer' }} >
                                  <TableCell onClick={(event) => event.stopPropagation()}>
                                    <Checkbox checked={selectedApplications.includes(application._id)} onChange={(event) => handleCheckboxChange(event, application._id)} />
                                  </TableCell>
                                  <TableCell> {index + 1} </TableCell>
                                  <TableCell> {application?.candidate_profile?.full_name || 'N/A'} </TableCell>
                                  <TableCell>
                                    {application?.tj_score >= 3 ? 'Highly Suitable' : application?.tj_score > 1 ? 'Moderately Suitable' : 'Not Suitable'}
                                  </TableCell>
                                  <TableCell> {application?.status || 'N/A'} </TableCell>
                                  <TableCell>
                                    {' '}
                                    {application?.requirements_matched?.qualifications?.join(' , ') || 'N/A'}
                                  </TableCell>
                                  <TableCell> {application?.requirements_matched?.experience || 'N/A'} </TableCell>
                                  <TableCell> {application?.requirements_matched?.salary || 'N/A'}</TableCell>
                                  {!application?.is_external_job_application && (
                                    <TableCell>
                                      {application?.resume_data?.title === 'Uploaded Resume' ? (
                                        <span
                                          onClick={() => handleDownload(application?.resume_data?.link, application?.candidate_profile)}
                                          style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                                        >
                                          Download Resume
                                        </span>
                                      ) : (
                                        <a
                                          href={`${process.env.REACT_APP_END_POINT}/api/services/download-resume?application_id=${application?._id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: '#1976d2', textDecoration: 'underline' }}
                                        >
                                          Download Resume
                                        </a>
                                      )}
                                    </TableCell>
                                  )}
                                  <TableCell>
                                    <a
                                      href={`${process.env.REACT_APP_END_POINT}/api/services/download-candidate-profile?application_id=${application?._id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#1976d2', textDecoration: 'underline' }}
                                    >
                                      Download Profile
                                    </a></TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={8}>No applications registered yet.</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  </Paper>
                </Box>
              </Container>
              <Drawer open={isBlurLoading}>
                <div className="loading-overlay">
                  <div className="loading-spinner">
                    <CircularProgress />
                  </div>
                </div>
              </Drawer>
            </Box>
          </Paper>
        </Container>
      </Box>
      <Box ref={applicantProfileRef}>
        {showApplicantProfile && selectedApplicant && (
          <Box sx={{ marginBottom: '1rem' }} >
            <Container>
              <Paper elevation={3} sx={{ padding: '1rem' }}>
                <ApplicantProfile selectedApplicant={selectedApplicant} onGoBack={handleGoBack} fetchApplicants={fetchApplicants} />
              </Paper>
            </Container>
          </Box>
        )}
      </Box>
    </>
  );
};

export default AllApplicantsList;
